import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { BILLING_STRINGS } from '../constants/billing-strings';

const COPY = BILLING_STRINGS.REVIEW;

export interface PromoCodeFieldProps {
  /** The code the backend accepted, as it stores it; null while none is applied. */
  applied: string | null;
  /** Why the last code was refused, shown beside the field. */
  error?: string;
  isApplying: boolean;
  onApply: (code: string) => void;
  onRemove: () => void;
  /** Called as the member types, so a stale refusal does not linger. */
  onEdit: () => void;
}

/**
 * A promo code on the review step, applied explicitly (Sprint 7 QA).
 *
 * "Apply" asks the backend to price the order with the code, so the member
 * sees the discount, or the reason it was refused, before paying. Until then
 * the typed text changes nothing; once applied, the code is shown with a way
 * to remove it rather than as an editable field.
 */
export function PromoCodeField({
  applied,
  error,
  isApplying,
  onApply,
  onRemove,
  onEdit,
}: PromoCodeFieldProps) {
  const [draft, setDraft] = useState('');
  const code = draft.trim();

  if (applied) {
    return (
      <div className="flex flex-wrap items-center gap-3" role="status">
        <p className="text-dkgreen text-sm font-medium">{COPY.PROMO_APPLIED.replace('{code}', applied)}</p>
        <Button
          variant="link"
          onClick={() => {
            setDraft('');
            onRemove();
          }}
        >
          {COPY.PROMO_REMOVE}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {/* The visible caption; the field carries the same text as its
          accessible name, so this is hidden from assistive technology. */}
      <p aria-hidden="true" className="text-text-primary text-p6 font-medium">
        {COPY.PROMO_LABEL}
      </p>
      <form
        className="flex items-start gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (code) onApply(code);
        }}
      >
        <div className="flex-1">
          <Input
            id="review-promo"
            label={COPY.PROMO_LABEL}
            value={draft}
            error={error}
            onChange={(event) => {
              setDraft(event.target.value);
              onEdit();
            }}
          />
        </div>
        <Button type="submit" variant="field-outline" disabled={!code || isApplying}>
          {COPY.PROMO_APPLY}
        </Button>
      </form>
    </div>
  );
}
